import { useEffect, useRef, useState, type ChangeEvent } from 'react';
import { useParams } from 'react-router-dom';
import { CERTIFICATE_REQUEST_CLOSED, CERTIFICATE_UPLOAD_MAX_BYTES, CERTIFICATE_UPLOAD_ROUTE, Color, Size, t, type PublicCertificateRequestAnswer } from '@bms2/shared';
import { Button } from '../components/controls';
import { Text } from '../components/Text';
import { PublicBand } from '../shell/PublicBand';
import { apiFetch, upload } from './records/api';
import styles from './certificateUpload.module.css';

/** The link's two public routes, relative to the API root (decision 0679). */
export const PUBLIC_CERTIFICATE_REQUESTS_PATH = '/public/certificate-requests';
export { CERTIFICATE_UPLOAD_ROUTE };

type State =
  | { kind: 'reading' }
  | { kind: 'closed'; message: string }
  | { kind: 'ready'; facts: PublicCertificateRequestAnswer }
  | { kind: 'received' };

/**
 * Where a certificate request link lands (piece 10; decision 0679). 🔨 The
 * page stands outside the shell and holds no session: the customer's contact
 * who opens it has no login and needs none. 🔒 It shows the company name and
 * the certificate kind the server answers and nothing else — no site, no
 * address, no person — and takes one PDF or photo. The server decides every
 * refusal; the size is checked here too so a phone is not asked to send ten
 * megabytes it would be refused.
 */
export function CertificateUploadPage({ apiBaseUrl = '' }: { apiBaseUrl?: string }) {
  const { token = '' } = useParams();
  const [state, setState] = useState<State>({ kind: 'reading' });
  const [file, setFile] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const input = useRef<HTMLInputElement>(null);
  const path = `${PUBLIC_CERTIFICATE_REQUESTS_PATH}/${encodeURIComponent(token)}`;

  useEffect(() => {
    const controller = new AbortController();
    apiFetch(`${apiBaseUrl}${path}`, { signal: controller.signal })
      .then(async (response) => {
        const body = (await response.json().catch(/* silent: a page open without a session posts no report (piece 9: a report is posted only by a signed-in person). */ () => null)) as (Partial<PublicCertificateRequestAnswer> & { message?: string }) | null;
        if (!response.ok || !body?.companyName) return setState({ kind: 'closed', message: body?.message ?? CERTIFICATE_REQUEST_CLOSED });
        return setState({ kind: 'ready', facts: { companyName: body.companyName, kind: body.kind ?? '' } });
      })
      .catch(/* silent: a page open without a session posts no report (piece 9: a report is posted only by a signed-in person). */ () => {
        if (!controller.signal.aborted) setState({ kind: 'closed', message: CERTIFICATE_REQUEST_CLOSED });
      });
    return () => controller.abort();
  }, [apiBaseUrl, path]);

  const pick = (event: ChangeEvent<HTMLInputElement>) => {
    setError(null);
    setFile(event.target.files?.[0] ?? null);
  };

  const send = async () => {
    setError(null);
    if (!file) return setError(t('certificateUpload.noFile'));
    if (file.size > CERTIFICATE_UPLOAD_MAX_BYTES) return setError(t('certificateUpload.tooLarge'));
    const form = new FormData();
    form.append('file', file, file.name);
    setBusy(true);
    const answer = await upload<{ received: true }>(`${path}/upload`, form, apiBaseUrl);
    setBusy(false);
    if (!answer.ok) return setError(answer.message);
    setState({ kind: 'received' });
  };

  return (
    <main className={styles.page} data-page="certificate-upload">
      {/* 🔒 The band every page outside the shell opens on, so the contact sees whose request this is (decision 0690). */}
      <PublicBand />
      <div className={styles.body}>
        <div className={styles.panel} data-certificate-upload={state.kind}>
          <Text variant="title">{t('certificateUpload.title')}</Text>
          {state.kind === 'reading' ? <Text variant="small">{t('certificateUpload.reading')}</Text> : null}
          {state.kind === 'closed' ? <Text variant="body">{state.message}</Text> : null}
          {state.kind === 'received' ? <Text variant="body">{t('certificateUpload.received')}</Text> : null}
          {state.kind === 'ready' ? (
            <>
              <dl className={styles.facts}>
                <dt>{t('certificateUpload.for')}</dt>
                <dd data-certificate-upload-company="true">{state.facts.companyName}</dd>
                <dt>{t('certificateUpload.document')}</dt>
                <dd data-certificate-upload-kind="true">{state.facts.kind}</dd>
              </dl>
              <Text variant="caption">{t('certificateUpload.hint')}</Text>
              <div className={styles.pick}>
                <Button size={Size.Medium} color={Color.Navy} text={[t('certificateUpload.choose')]} actions={[() => input.current?.click()]} />
                <span className={styles.fileName} data-certificate-upload-file="true">{file?.name ?? t('certificateUpload.noFile')}</span>
              </div>
              <input ref={input} className={styles.hiddenInput} type="file" accept="application/pdf,image/*" aria-label={t('certificateUpload.choose')} onChange={pick} data-certificate-upload-input="true" />
              {error ? <p className={styles.error} role="alert">{error}</p> : null}
              <Button size={Size.Large} color={Color.Navy} text={[t(busy ? 'certificateUpload.sending' : 'certificateUpload.send')]} actions={[() => void send()]} disabled={busy || !file} />
            </>
          ) : null}
        </div>
      </div>
    </main>
  );
}
